import i18next from "i18next";
import { initReactI18next, useTranslation } from "react-i18next";
import { useCallback } from "react";
import { en, type MessageKey } from "./locales/en";
import { th } from "./locales/th";

export type Locale = "th" | "en";
const STORAGE_KEY = "console.locale";

export function storedLocale(): Locale {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "th" || v === "en") return v;
  } catch {
    // storage unavailable
  }
  return "th";
}

export async function initI18n(locale: Locale = storedLocale()) {
  await i18next.use(initReactI18next).init({
    lng: locale,
    fallbackLng: "en",
    resources: { en: { translation: en }, th: { translation: th } },
    interpolation: { escapeValue: false },
    keySeparator: false,
    nsSeparator: false,
  });
  document.documentElement.lang = locale;
  return i18next;
}

export function setLocale(locale: Locale) {
  try {
    localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    // ignore
  }
  document.documentElement.lang = locale;
  void i18next.changeLanguage(locale);
}

export function currentLocale(): Locale {
  return i18next.language === "en" ? "en" : "th";
}

export type TFunc = (key: MessageKey, vars?: Record<string, string | number>) => string;

/** Typed translation hook: only known keys compile. */
export function useT(): { t: TFunc; locale: Locale } {
  const { t, i18n } = useTranslation();
  const tf = useCallback<TFunc>((key, vars) => t(key, vars ?? {}), [t]);
  return { t: tf, locale: i18n.language === "en" ? "en" : "th" };
}

export type { MessageKey };
